// SpecPicker.tsx
// Pick specs the way the Alignment sign up form does: a row of class icons,
// then the open class's specs. Used by the guild Recruiting panel (who we are
// looking for) and the raid Open seats panel (which specs may take a seat).
// Classes and specs come from the game's own options, so any game works.
import React, { useState } from "react";
import { Icon, tc } from "./alignmentUi";

export type PickerOption = { id: number; key: string; label: string; metadata?: any; image_url?: string | null };

export default function SpecPicker({
  classes, specs, selected, onToggle, className = "",
}: {
  classes: PickerOption[];
  specs: PickerOption[];
  selected: number[];
  onToggle: (specId: number) => void;
  className?: string;
}) {
  const [openClass, setOpenClass] = useState<string | null>(null);
  const inClass = (classKey: string) =>
    specs.filter((s) => s.metadata?.class_key === classKey && selected.includes(s.id)).length;

  return (
    <div className={className}>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-9">
        {classes.map((c) => {
          const n = inClass(c.key);
          const open = openClass === c.key;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setOpenClass(open ? null : c.key)}
              className={`relative flex flex-col items-center gap-1.5 rounded-2xl border px-2 py-2.5 transition ${open ? "border-[var(--c-accent)] bg-[var(--c-sunken)]" : "border-[var(--c-line)] hover:bg-[var(--c-sunken)]"}`}
            >
              <Icon src={c.image_url} alt={c.label} size={32} />
              <span className="text-[12px] font-semibold" style={{ color: tc(c.metadata?.color || undefined) }}>{c.label}</span>
              {n > 0 && <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--c-accent)] px-1 text-[10px] font-bold text-[var(--c-ink-on-fill)]">{n}</span>}
            </button>
          );
        })}
      </div>
      {openClass && (
        <div className="mt-3 flex flex-wrap gap-2 rounded-2xl bg-[var(--c-sunken)] p-3">
          {specs.filter((s) => s.metadata?.class_key === openClass).map((s) => {
            const on = selected.includes(s.id);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onToggle(s.id)}
                aria-pressed={on}
                className={`flex items-center gap-2 rounded-xl border py-1 pl-1 pr-3 text-[13px] font-semibold transition ${on ? "border-[var(--c-accent)] bg-[var(--c-surface)] text-[var(--c-ink)]" : "border-[var(--c-line)] text-[var(--c-ink-2)] hover:bg-[var(--c-surface)]"}`}
              >
                <Icon src={s.image_url} alt={s.label} size={26} />
                {s.label}
                {on && <span className="text-[var(--c-accent-ink)]">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
